import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  useAuditLogs,
  useAuditStats,
  useTimeSignatures,
  useAuditExports,
} from '@/hooks/useTimeTrackingAudit';
import { AuditAction, AuditRecord } from '@/types/timeTracking';
import {
  Shield,
  FileText,
  Search,
  Calendar,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Filter,
  BarChart3,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import { format, subDays, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const ACTION_LABELS: Record<AuditAction, string> = {
  clock_in: 'Entrada',
  clock_out: 'Salida',
  break_start: 'Inicio Pausa',
  break_end: 'Fin Pausa',
  manual_entry: 'Entrada Manual',
  correction: 'Corrección',
  deletion: 'Eliminación',
  approval: 'Aprobación',
  signature: 'Firma',
  export: 'Exportación',
  view_report: 'Ver Informe',
};

const ACTION_COLORS: Record<AuditAction, string> = {
  clock_in: 'bg-green-100 text-green-800',
  clock_out: 'bg-red-100 text-red-800',
  break_start: 'bg-yellow-100 text-yellow-800',
  break_end: 'bg-blue-100 text-blue-800',
  manual_entry: 'bg-purple-100 text-purple-800',
  correction: 'bg-orange-100 text-orange-800',
  deletion: 'bg-red-200 text-red-900',
  approval: 'bg-emerald-100 text-emerald-800',
  signature: 'bg-indigo-100 text-indigo-800',
  export: 'bg-cyan-100 text-cyan-800',
  view_report: 'bg-gray-100 text-gray-800',
};

export function TimeTrackingAuditDashboard() {
  const { toast } = useToast();
  const [dateFrom, setDateFrom] = useState(format(subMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditRecord | null>(null);

  const { data: stats, isLoading: statsLoading } = useAuditStats(dateFrom, dateTo);
  const { data: logs, isLoading: logsLoading, refetch } = useAuditLogs({
    dateFrom,
    dateTo,
    action: actionFilter !== 'all' ? actionFilter as AuditAction : undefined,
  });
  const { data: signatures } = useTimeSignatures();
  const { data: exports } = useAuditExports();

  const filteredLogs = logs?.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.user?.full_name?.toLowerCase().includes(search) ||
      log.performer?.full_name?.toLowerCase().includes(search) ||
      log.reason?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Auditoría de Fichajes
          </h2>
          <p className="text-muted-foreground mt-1">
            Control y trazabilidad conforme a RDL 8/2019
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Registros</p>
                <p className="text-2xl font-bold">{stats?.total_records || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Correcciones</p>
                <p className="text-2xl font-bold text-orange-600">
                  {stats?.corrections_count || 0}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Firmas Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats?.signatures_pending || 0}
                </p>
              </div>
              <PenLine className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Firmas Completadas</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats?.signatures_completed || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="logs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="logs" className="gap-2">
            <FileText className="w-4 h-4" />
            Registro de Auditoría
          </TabsTrigger>
          <TabsTrigger value="signatures" className="gap-2">
            <PenLine className="w-4 h-4" />
            Firmas
          </TabsTrigger>
          <TabsTrigger value="exports" className="gap-2">
            <Download className="w-4 h-4" />
            Exportaciones
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Estadísticas
          </TabsTrigger>
        </TabsList>

        {/* Audit Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label>Buscar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Nombre, motivo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="w-full md:w-40">
                  <Label>Desde</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="w-full md:w-40">
                  <Label>Hasta</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="w-full md:w-48">
                  <Label>Tipo de Acción</Label>
                  <Select value={actionFilter} onValueChange={setActionFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {Object.entries(ACTION_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              ) : filteredLogs?.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay registros de auditoría</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha/Hora</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Acción</TableHead>
                      <TableHead>Realizado por</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead className="text-right">Detalles</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs?.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-sm">
                          {format(new Date(log.performed_at), "dd/MM/yyyy HH:mm:ss")}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            {log.user?.full_name || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(ACTION_COLORS[log.action])}>
                            {ACTION_LABELS[log.action]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.performer?.full_name || '-'}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {log.reason || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Signatures Tab */}
        <TabsContent value="signatures" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Firmas de Períodos</CardTitle>
              <CardDescription>
                Registro de confirmaciones de horas trabajadas por empleados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {signatures?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <PenLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay firmas registradas</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empleado</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Horas Totales</TableHead>
                      <TableHead>Días</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha Firma</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signatures?.map((sig) => (
                      <TableRow key={sig.id}>
                        <TableCell>
                          {sig.user?.full_name || '-'}
                        </TableCell>
                        <TableCell>
                          {format(new Date(sig.period_start), 'dd/MM/yyyy')} -{' '}
                          {format(new Date(sig.period_end), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="font-mono">
                          {sig.total_hours_worked.toFixed(2)}h
                        </TableCell>
                        <TableCell>{sig.total_days_worked}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              sig.status === 'signed' ? 'default' :
                              sig.status === 'disputed' ? 'destructive' :
                              sig.status === 'approved' ? 'outline' : 'secondary'
                            }
                          >
                            {sig.status === 'signed' ? 'Firmado' :
                             sig.status === 'disputed' ? 'Disputado' :
                             sig.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {sig.signed_at
                            ? format(new Date(sig.signed_at), 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exports Tab */}
        <TabsContent value="exports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Exportaciones</CardTitle>
              <CardDescription>
                Registro de todas las exportaciones de datos de fichaje
              </CardDescription>
            </CardHeader>
            <CardContent>
              {exports?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Download className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay exportaciones registradas</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Formato</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Registros</TableHead>
                      <TableHead>Propósito</TableHead>
                      <TableHead>Solicitado por</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exports?.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell>
                          {format(new Date(exp.requested_at), 'dd/MM/yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{exp.export_type}</Badge>
                        </TableCell>
                        <TableCell className="uppercase font-mono">
                          {exp.format}
                        </TableCell>
                        <TableCell>
                          {format(new Date(exp.date_from), 'dd/MM')} -{' '}
                          {format(new Date(exp.date_to), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell>{exp.record_count || '-'}</TableCell>
                        <TableCell>
                          {exp.purpose === 'internal_audit' ? 'Auditoría Interna' :
                           exp.purpose === 'labor_inspection' ? 'Inspección Laboral' :
                           exp.purpose === 'employee_request' ? 'Solicitud Empleado' : '-'}
                        </TableCell>
                        <TableCell>{exp.requester?.full_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Acciones por Tipo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.by_action && Object.entries(stats.by_action).map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between">
                      <Badge className={cn(ACTION_COLORS[action as AuditAction])}>
                        {ACTION_LABELS[action as AuditAction]}
                      </Badge>
                      <span className="font-mono font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Usuarios con más Actividad</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.by_user?.map((user, idx) => (
                    <div key={user.user_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{idx + 1}.</span>
                        <span>{user.full_name}</span>
                      </div>
                      <span className="font-mono font-bold">{user.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de Registro de Auditoría</DialogTitle>
            <DialogDescription>
              ID: {selectedLog?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Fecha/Hora</Label>
                  <p className="font-mono">
                    {format(new Date(selectedLog.performed_at), "dd/MM/yyyy HH:mm:ss")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Acción</Label>
                  <Badge className={cn(ACTION_COLORS[selectedLog.action])}>
                    {ACTION_LABELS[selectedLog.action]}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Usuario Afectado</Label>
                  <p>{selectedLog.user?.full_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Realizado por</Label>
                  <p>{selectedLog.performer?.full_name || '-'}</p>
                </div>
              </div>

              {selectedLog.reason && (
                <div>
                  <Label className="text-muted-foreground">Motivo</Label>
                  <p>{selectedLog.reason}</p>
                </div>
              )}

              {selectedLog.old_data && (
                <div>
                  <Label className="text-muted-foreground">Datos Anteriores</Label>
                  <pre className="mt-1 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.old_data, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.new_data && (
                <div>
                  <Label className="text-muted-foreground">Datos Nuevos</Label>
                  <pre className="mt-1 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.new_data, null, 2)}
                  </pre>
                </div>
              )}

              {(selectedLog.ip_address || selectedLog.user_agent) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedLog.ip_address && (
                    <div>
                      <Label className="text-muted-foreground">IP</Label>
                      <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                    </div>
                  )}
                  {selectedLog.user_agent && (
                    <div>
                      <Label className="text-muted-foreground">User Agent</Label>
                      <p className="text-sm truncate">{selectedLog.user_agent}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TimeTrackingAuditDashboard;
